import React, { useState } from 'react';
import { SlideFooter } from '../components/canvas/SlideFooter';
import { TITLE_FONT_SIZE_LEVELS, DEFAULT_TITLE_FONT_SIZE, BODY_FONT_SIZE_LEVELS, DEFAULT_BODY_FONT_SIZE, KICKER_FONT_SIZE_LEVELS, DEFAULT_KICKER_FONT_SIZE, SUBTITLE_FONT_SIZE_LEVELS, DEFAULT_SUBTITLE_FONT_SIZE, STATS_FONT_SIZE_LEVELS, DEFAULT_STATS_FONT_SIZE, TAGS_FONT_SIZE_LEVELS, DEFAULT_TAGS_FONT_SIZE } from '../constants/typography';
import { renderFormattedText } from '../utils/formatText';
import { usePreviewInteraction } from '../contexts/PreviewInteractionContext';
import { InlineEditable } from '../components/canvas/InlineEditable';
import { NudgeableGroup } from '../components/canvas/NudgeableGroup';

/**
 * Normies GM layout — Bold daily GM post.
 *
 * Big Normie image centered, huge "GM NORMIES" title,
 * persona greeting, date line. Simple, confident, fills the canvas.
 */
export function NormiesGmLayout({ data, fontPreset, pc }) {
  const { interactive, onElementClick, editingField, onEditChange, onEndEdit } = usePreviewInteraction();
  const [imgLoaded, setImgLoaded] = useState(false);

  const titleSize = TITLE_FONT_SIZE_LEVELS[data.titleFontSize ?? DEFAULT_TITLE_FONT_SIZE]?.size || 74;
  const bodySize = BODY_FONT_SIZE_LEVELS[data.bodyFontSize ?? DEFAULT_BODY_FONT_SIZE]?.size || 32;
  const kickerSize = KICKER_FONT_SIZE_LEVELS[data.kickerFontSize ?? DEFAULT_KICKER_FONT_SIZE]?.size || 16;
  const subtitleSize = SUBTITLE_FONT_SIZE_LEVELS[data.subtitleFontSize ?? DEFAULT_SUBTITLE_FONT_SIZE]?.size || 20;
  const tagsSize = TAGS_FONT_SIZE_LEVELS[data.tagsFontSize ?? DEFAULT_TAGS_FONT_SIZE]?.size || 11;
  const statsSize = STATS_FONT_SIZE_LEVELS[data.statsFontSize ?? DEFAULT_STATS_FONT_SIZE]?.size || 20;

  const heroImage = data.normies?.heroImage;

  const handleTitleClick = (e) => {
    if (!interactive || !onElementClick) return;
    if (editingField === 'title') return;
    e.stopPropagation();
    onElementClick('header', 'title');
  };

  return (
    <>
      {/* Warm overlay */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.10) 30%, rgba(0,0,0,0.10) 60%, rgba(0,0,0,0.6) 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* Kicker — top left */}
      <NudgeableGroup groupId="content" className="absolute top-[60px] left-[60px] right-[60px] bottom-[90px]" style={{ display: 'flex', flexDirection: 'column' }}>
        {data.kickerEnabled !== false && data.kicker && (
          <div
            style={{
              fontFamily: fontPreset.subtitle || 'IBM Plex Mono, monospace',
              fontSize: `${Math.max(kickerSize, 18)}px`,
              fontWeight: 700,
              letterSpacing: '0.18em',
              color: pc.el.kicker || pc.textMuted,
              textTransform: 'uppercase',
              marginBottom: '16px',
            }}
          >
            {data.kicker}
          </div>
        )}

        {/* Title — huge, centered, bold */}
        <InlineEditable
          value={data.title}
          isEditing={editingField === 'title'}
          onChange={(html) => onEditChange && onEditChange('title', html)}
          onBlur={onEndEdit}
          className={`leading-[0.90] w-full${editingField === 'title' ? ' preview-editing' : (interactive ? ' preview-clickable' : '')}`}
          style={{
            fontSize: `${Math.max(titleSize, 80)}px`,
            fontFamily: fontPreset.title,
            fontWeight: fontPreset.titleWeight || 900,
            color: pc.el.title || pc.textPrimary,
            textTransform: 'uppercase',
            letterSpacing: '-0.04em',
            textAlign: 'center',
          }}
          onClick={handleTitleClick}
        >
          {data.title}
        </InlineEditable>

        {/* Large Normie hero — centered */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 0' }}>
          {heroImage && (
            <div style={{
              width: '480px',
              height: '480px',
              background: 'rgba(0,0,0,0.25)',
              border: `2px solid ${pc.accent(0.2)}`,
              borderRadius: '50%',
              overflow: 'hidden',
            }}>
              <img
                src={heroImage}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  imageRendering: 'pixelated',
                  opacity: imgLoaded ? 1 : 0,
                  transition: 'opacity 0.3s',
                }}
                onLoad={() => setImgLoaded(true)}
                crossOrigin="anonymous"
              />
            </div>
          )}
        </div>

        {/* Body — persona greeting */}
        {data.bodyEnabled !== false && data.body && (
          <div
            className="leading-[1.4]"
            style={{
              fontFamily: fontPreset.body || 'IBM Plex Mono, monospace',
              fontSize: `${Math.max(bodySize * 0.75, 18)}px`,
              color: pc.el.body || pc.textMuted,
              textAlign: 'center',
              marginBottom: '16px',
              fontStyle: 'italic',
              opacity: 0.85,
            }}
          >
            {"\u201C"}{renderFormattedText(data.body)}{"\u201D"}
          </div>
        )}

        {/* Subtitle — date line */}
        {data.subtitleEnabled !== false && data.subtitle && (
          <div
            style={{
              fontFamily: fontPreset.subtitle || 'IBM Plex Mono, monospace',
              fontSize: `${Math.max(subtitleSize * 0.8, 14)}px`,
              fontWeight: 700,
              color: pc.el.subtitle || pc.textSubtle,
              textAlign: 'center',
              letterSpacing: '0.12em',
              marginBottom: '12px',
            }}
          >
            {data.subtitle}
          </div>
        )}

        {/* Tags */}
        {data.tagsEnabled !== false && data.meta && data.meta.length > 0 && (
          <div className="flex flex-wrap gap-3 justify-center">
            {data.meta.map((tag, i) => (
              <span
                key={i}
                style={{
                  fontFamily: 'IBM Plex Mono, monospace',
                  fontSize: `${Math.max(tagsSize, 12)}px`,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  color: pc.el.tagText || pc.textMuted,
                  border: `1px solid ${pc.el.tagBorder || pc.accent(0.30)}`,
                  padding: '5px 14px',
                  textTransform: 'uppercase',
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </NudgeableGroup>

      {/* Footer */}
      <div className="absolute bottom-[36px] left-[60px] right-[60px]">
        <SlideFooter data={data} pc={pc} />
      </div>
    </>
  );
}
